/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { TimeSinceLastGroomEnum } from '@rover/rsdk/src/apiClient/latest/model/timeSinceLastGroomEnum';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

import type { RecurringScheduleSource } from './constants';

abstract class GroomingEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.grooming;
}

type GroomingPages =
  | 'experience'
  | 'yearsExperience'
  | 'signUpHub'
  | 'groomingGate'
  | 'groomingSettings'
  | 'groomingProfile'
  | 'groomingAddTestimonials'
  | 'groomingAddPhotos'
  | 'groomingSafetyQuiz'
  | 'groomingProfileSubmitted';

type GroomingSteps = 'settings' | 'profile' | 'testimonials' | 'photos' | 'safetyQuiz';

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views one of the grooming sign up pages, fire an event.
 *
 * &nbsp;
 */
export class GroomingSignUpPageView extends GroomingEvent {
  eventName = 'grooming-sign-up-page-view';

  constructor(eventProperties: { page: GroomingPages }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks save/done on any of the sign up page steps, fire an event.
 *
 * &nbsp;
 */
export class GroomingSignUpStepComplete extends GroomingEvent {
  eventName = 'grooming-sign-up-step-complete';

  constructor(eventProperties: { stepCompleted: GroomingSteps }) {
    const observabilityTags = eventProperties; // rename for clarity
    // passing in observabilityTags for Datadog logs, and eventProperties for the analytics event itself
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to start the safety & skills quiz, fire an event.
 *
 * &nbsp;
 */
export class GroomingSafetyQuizStart extends GroomingEvent {
  eventName = 'grooming-safety-quiz-start';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to start the safety & skills quiz, fire an event.
 *
 * &nbsp;
 */
export class GroomingSafetyQuizCompleted extends GroomingEvent {
  eventName = 'grooming-safety-quiz-completed';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views a groomer contact page, fire an event.
 * This is primarily being added to support SEM tracking.
 *
 * &nbsp;
 */
export class GroomingContactPageView extends GroomingEvent {
  eventName = 'grooming-contact-page-view';

  consentPurpose: ConsentPurpose = 'marketing';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views a groomer profile page, fire an event.
 *  This is primarily being added to support SEM tracking.
 *
 * &nbsp;
 */
export class GroomingProfilePageView extends GroomingEvent {
  eventName = 'grooming-profile-page-view';

  consentPurpose: ConsentPurpose = 'marketing';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user selects "I'm flexible on dates", fire an event.
 *
 * &nbsp;
 */
export class GroomingContactFlexDatesClick extends GroomingEvent {
  eventName = 'grooming-contact-flex-dates-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When the "Send request" button becomes visible on the page, fire an event.
 * This tracks when a user reaches the end of the contact form.
 *
 * &nbsp;
 */
export class GroomingContactScroll extends GroomingEvent {
  eventName = 'grooming-contact-scroll';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user selects an option for "When's the last time your pet was groomed?", fire an event.
 *
 * &nbsp;
 */
export class GroomingContactLastGroomSelect extends GroomingEvent {
  eventName = 'grooming-contact-last-groom-select';

  constructor(eventProperties: { buttonSelect: TimeSinceLastGroomEnum }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks on the contact button on the groomer profile page, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfileContactClick extends GroomingEvent {
  eventName = 'grooming-profile-contact-click';

  constructor(eventProperties: { button: 'primaryDesktop' | 'footer' }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to open the photo gallery, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfilePhotosClick extends GroomingEvent {
  eventName = 'grooming-profile-photos-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to read more content, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfileReadMoreClick extends GroomingEvent {
  // Note: event spec uses 'grooming-profile-contact-click' for this event name
  eventName = 'grooming-profile-contact-click';

  constructor(eventProperties: {
    location: 'about' | 'serviceDescription' | 'availability' | 'review' | 'allReviews';
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks to view all services reviews, fire an event.
 *
 * &nbsp;
 */
export class GroomingProfileAllServicesReviewsClick extends GroomingEvent {
  eventName = 'grooming-profile-all-services-reviews-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views the grooming search page, fire an event. This should only
 * fire on page load with data, and does not need to fire each time someone
 * filters the search results. Don't fire in the event we have an error loading
 * page data. This is primarily being added to support SEM tracking.
 *
 * &nbsp;
 */
export class GroomingSearchPageView extends GroomingEvent {
  eventName = 'grooming-search-page-view';

  consentPurpose: ConsentPurpose = 'marketing';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When the grooming mobile additional-filters modal opens, fire an event.
 *
 * &nbsp;
 */
export class GroomingAdditionalFiltersView extends GroomingEvent {
  eventName = 'grooming-additional-filters-view';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks on a groomer search card, fire an event.
 *
 * &nbsp;
 */
export type GroomingSearchCardSection = 'Recommended-professionals' | 'Other-professionals';

export class GroomingSearchCardClick extends GroomingEvent {
  eventName = 'grooming-search-card-click';

  constructor(eventProperties: { section: GroomingSearchCardSection }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks the "Add rates" button on the grooming settings page, fire an event.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-click';

  constructor(eventProperties: { button: 'add-rates' | 'manage-rates' }) {
    super(eventProperties);
  }
}

/** Service identifier used across the grooming rate-setup tracking events. */
export type GroomingRatesServiceType = 'bathAndBrush' | 'fullGroom';

/** Pet size identifier used across the grooming rate-setup tracking events. */
export type GroomingRatesPetSize = 'xs' | 's' | 'm' | 'l' | 'xl' | '2xl';

/**
 * &nbsp;
 *
 * # Description
 *
 * When a groomer clicks the primary CTA ("Suggest rates for me") in the rate
 * setup intro modal to auto-calculate their rate matrix.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesCalcSelectClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-calc-select-click';

  constructor(eventProperties: { service: GroomingRatesServiceType }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a groomer clicks the secondary CTA ("Add rates manually") in the rate
 * setup intro modal to enter their rates by hand.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesManualSelectClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-manual-select-click';

  constructor(eventProperties: { service: GroomingRatesServiceType }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a groomer clicks the edit control on a pet-size card in the full rate
 * matrix.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesSizeEditClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-size-edit-click';

  constructor(eventProperties: {
    service: GroomingRatesServiceType;
    petSize: GroomingRatesPetSize;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a groomer saves the coat prices for a single pet size from the edit-rate
 * modal.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesSizeSaveClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-size-save-click';

  constructor(eventProperties: {
    service: GroomingRatesServiceType;
    petSize: GroomingRatesPetSize;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a groomer taps "Edit rates" on a service that already has rates to
 * reopen its full rate matrix.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesEditClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-edit-click';

  constructor(eventProperties: { service: GroomingRatesServiceType }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a groomer opens the rate calculator from the full rate matrix via the
 * "Suggest rates for me" control.
 *
 * &nbsp;
 */
export class GroomingSettingsRatesViewCalcClick extends GroomingEvent {
  eventName = 'grooming-settings-rates-view-calc-click';

  constructor(eventProperties: { service: GroomingRatesServiceType }) {
    super(eventProperties);
  }
}

type RecurringScheduleUser = 'owner' | 'groomer';

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views the select repeat schedule sheet, fire an event.
 *
 * &nbsp;
 */
export class GroomingRecurringSelectScheduleViewed extends GroomingEvent {
  eventName = 'grooming-recurring-select-schedule-viewed';

  constructor(eventProperties: { user: RecurringScheduleUser; source: RecurringScheduleSource }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When an owner clicks set schedule on the select repeat schedule sheet, fire an event.
 *
 * &nbsp;
 */
export class GroomingRecurringSelectScheduleSetClick extends GroomingEvent {
  eventName = 'grooming-recurring-select-schedule-set-click';

  constructor(eventProperties: {
    user: RecurringScheduleUser;
    source: RecurringScheduleSource;
    frequency_weeks: number;
    is_recommended_frequency: boolean;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When an owner clicks "I'll set this up later" on the select repeat schedule sheet, fire an event.
 *
 * &nbsp;
 */
export class GroomingRecurringSelectScheduleLaterClick extends GroomingEvent {
  eventName = 'grooming-recurring-select-schedule-later-click';

  constructor(eventProperties: { user: RecurringScheduleUser; source: RecurringScheduleSource }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views the grooming unavailable page, fire an event.
 *
 * &nbsp;
 */
export class GroomingUnavailablePageView extends GroomingEvent {
  eventName = 'grooming-unavailable-page-viewed';

  constructor(eventProperties: { zipCode: string | undefined }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks "Join the waitlist" on the grooming unavailable page, fire
 * an event.
 *
 * &nbsp;
 */
export class GroomingUnavailableJoinWaitlistClick extends GroomingEvent {
  eventName = 'grooming-unavailable-joinwaitlist-clicked';

  constructor(eventProperties: { zipCode: string | undefined }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks "Select another service" on the grooming unavailable page,
 * fire an event.
 *
 * &nbsp;
 */
export class GroomingUnavailableSelectAnotherServiceClick extends GroomingEvent {
  eventName = 'grooming-unavailable-selectanotherservice-clicked';

  constructor(eventProperties: { zipCode: string | undefined }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user views the join the groomer waitlist form, fire an event.
 *
 * &nbsp;
 */
export class GroomingWaitlistPageView extends GroomingEvent {
  eventName = 'grooming-waitlist-page-viewed';

  constructor(eventProperties: { zipCode: string | undefined }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user submits the groomer waitlist form, fire an event.
 *
 * &nbsp;
 */
export class GroomingWaitlistSubmitClick extends GroomingEvent {
  eventName = 'grooming-waitlist-submit-clicked';

  constructor(eventProperties: { zipCode: string | undefined }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks the grooming search button on the mweb homepage service selector.
 *
 * &nbsp;
 */
export class GroomingHomepageSearchClick extends GroomingEvent {
  eventName = 'grooming_homepage_search_click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * When a user clicks the grooming service entry on the desktop rebooking homepage.
 *
 * &nbsp;
 */
export class GroomingHomepageRebookServiceClick extends GroomingEvent {
  eventName = 'grooming_homepage_rebook_service_grooming_click';
}
